import React, { useContext, useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { VariantContextData } from "../variants/VariantBuilderContext";
import templateableElementTypesArray from "../../utils/templateableElementTypesArray";
import RemixSparkleIcon from "../variants/ai/RemixSparkleIcon";
import { setAiChatFocus } from "../variants/ai/useAiAssistant";
import { ROW_TYPES_WITHOUT_POPOVER } from "../../utils/canvasSelectLabel";
import { hasPendingDuplicate } from "../../utils/builderUtils";

// Singleton popover component
const VariantBuilderCanvasSelectPopover = () => {
  const [contextData, builder] = useContext(VariantContextData);
  const [open, setOpen] = useState(false);
  const [popoverProps, setPopoverProps] = useState({});
  const popoverRef = useRef(null);

  // Listen for custom showPopover event
  useEffect(() => {
    function handleShowPopover(e) {
      const detail = e.detail || {};
      setPopoverProps(detail);
      setOpen(true);
    }
    document.addEventListener("showPopover", handleShowPopover);
    return () => document.removeEventListener("showPopover", handleShowPopover);
  }, []);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    function handleClick(event) {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setOpen(false);
        setPopoverProps({});
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  let showDuplicateButton = ["steps", "sections", "elements"].includes(
    popoverProps.object_type
  );
  let showRemixButton = [
    "steps",
    "sections",
    "section_columns",
    "elements",
    "fields",
  ].includes(popoverProps.object_type);
  let showRemoveButton = true;
  let showMoveButtons = popoverProps.object_type !== "section_columns";
  let showResetButton = ["sections", "elements"].includes(popoverProps.object_type);
  let object_type = popoverProps.object_type;
  let object_id = popoverProps.object_id;
  let hidePopover = false;
  let rowObject;
  let elementObject;

  if (
    popoverProps.object_type == "sections" &&
    popoverProps.object &&
    popoverProps.object.element_id
  ) {
    rowObject = contextData.objects.variant[popoverProps.object_type][popoverProps.object_id];
    elementObject = contextData.objects.variant.elements[rowObject.element_id];

    object_type = "elements";
    object_id = elementObject.id;

    if (ROW_TYPES_WITHOUT_POPOVER.includes(elementObject.element_type)) {
      hidePopover = true;
    }
  }

  showDuplicateButton =
    showDuplicateButton &&
    !hasPendingDuplicate(contextData.objects.variant, object_type, object_id);

  const selectedElement =
    elementObject ||
    (popoverProps.object_type == "elements"
      ? contextData.objects.variant.elements[popoverProps.object_id] ||
        popoverProps.object
      : undefined);

  const canSaveTemplate =
    (["steps", "sections", "elements"].includes(popoverProps.object_type) &&
      (!selectedElement ||
        templateableElementTypesArray.includes(
          selectedElement.element_type
        ))) ||
    (popoverProps.object_type == "fields" &&
      ["tech", "marketing"].includes(
        contextData.objects.current_user.admin_type
      ));

  const saveTemplate = () => {
    // For sections belonging to row elements, save the row element instead
    let objectType = popoverProps.object_type;
    let objectId = popoverProps.object_id;

    if (
      popoverProps.object_type == "sections" &&
      popoverProps.object &&
      popoverProps.object.element_id
    ) {
      const rowObject = contextData.objects.variant[popoverProps.object_type][popoverProps.object_id];
      const elementObject = contextData.objects.variant.elements[rowObject.element_id];
      
      objectType = "elements";
      objectId = elementObject.id;
    }

    const targetController =
      objectType == "steps"
        ? "step_templates"
        : objectType == "sections"
        ? "section_templates"
        : objectType == "elements"
        ? "element_templates"
        : objectType == "fields"
        ? "field_templates"
        : null;
    const targetObjectType =
      objectType == "steps"
        ? "Step"
        : objectType == "sections"
        ? "Section"
        : objectType == "elements"
        ? "Element"
        : objectType == "fields"
        ? "Field"
        : null;

    if (contextData.history.unsaved == true) {
      alert(
        "You have unsaved changes. Save your changes to enable saving templates."
      );
    } else {
      const baseRoute = `${window.location.origin}/accounts/${contextData.objects.website.account_id}/${targetController}/new`;
      const routeParams = `?object_type=${targetObjectType}&object_id=${objectId}`;

      window.open(baseRoute + routeParams, "_blank");
    }
  };

  const waitingClick = useRef(false);

  const clickWrapper = (event, action) => {
    event.stopPropagation();
    if (waitingClick.current) return;
    waitingClick.current = true;
    action();
    setTimeout(() => {
      waitingClick.current = false;
    }, 500);
    setOpen(false);
    setPopoverProps({});
  };

  if (!open || hidePopover) return null;

  let leftPosition = "50%";
  let topPosition = "50%";
  let popoverTransform = popoverProps.anchorPosition
    ? undefined
    : "translate(-50%, -50%)";

  if (popoverProps.anchorPosition) {
    const popoverWidth = 230;
    const popoverHeight = 360;
    const viewportPadding = 12;

    leftPosition = Math.min(
      Math.max(popoverProps.anchorPosition.x, viewportPadding),
      window.innerWidth - popoverWidth - viewportPadding
    );
    topPosition = Math.min(
      Math.max(popoverProps.anchorPosition.y, viewportPadding),
      window.innerHeight - popoverHeight - viewportPadding
    );
  }

  let popoverStyle = {
    display: "block",
    borderRadius: "10px",
    position: "fixed",
    left: leftPosition,
    top: topPosition,
    transform: popoverTransform,
    transformOrigin: "top left",
    zIndex: 1000,
  };

  return (
    <div
      ref={popoverRef}
      className={`select-label-popover cursor-pointer bg-white dark:bg-black border-solid border-[1px] border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden z-10`}
      data-object_type={popoverProps.object_type}
      data-object_id={popoverProps.object_id}
      style={popoverStyle}
    >
      <div className="flex flex-col">

        <button
          title="Edit"
          className="flex items-center px-4 py-3 text-lg bg-white hover:bg-slate-100 dark:bg-black dark:hover:bg-slate-800 border-none cursor-pointer w-full text-left text-black dark:text-white"
          onClick={(e) => {
            clickWrapper(e, () => {
              dispatchCustomEvent("selectObject", {
                object_type: popoverProps.object_type,
                object_id: popoverProps.object_id,
                tab: popoverProps.tab,
                collapse: popoverProps.collapse,
              });
            });
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mr-3"
          >
            <path
              d="M10.0869 5.21357L12.7869 7.91357M3.33691 14.6636L6.6114 14.0038C6.78524 13.9688 6.94485 13.8832 7.0702 13.7577L14.4005 6.42346C14.7519 6.07182 14.7517 5.50183 14.3999 5.15048L12.8471 3.59943C12.4955 3.24823 11.9258 3.24846 11.5745 3.59996L4.24349 10.935C4.11838 11.0602 4.03295 11.2195 3.99789 11.3929L3.33691 14.6636Z"
              stroke={contextData.dark_mode ? "white" : "black"}
              strokeWidth="1.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Edit
        </button>

        {showRemixButton && (
          <button
            title="Remix with AI"
            className="flex items-center px-4 py-3 text-lg bg-white hover:bg-slate-100 dark:bg-black dark:hover:bg-slate-800 border-none cursor-pointer w-full text-left text-black dark:text-white"
            onClick={(e) => {
              clickWrapper(e, () => {
                setAiChatFocus(
                  contextData.objects.variant.id,
                  object_type,
                  object_id
                );
                builder.view("ai");
              });
            }}
          >
            <span className="mr-3 flex items-center">
              <RemixSparkleIcon width={16} height={17} />
            </span>
            Remix with AI
          </button>
        )}

        
        {showMoveButtons && (
          <React.Fragment>
            <button
              title="Move Up"
              className="flex items-center px-4 py-3 text-lg bg-white hover:bg-slate-100 dark:bg-black dark:hover:bg-slate-800 border-none cursor-pointer w-full text-left text-black dark:text-white"
              onClick={(e) => {
                clickWrapper(e, () => {
                  dispatchCustomEvent("moveObjectUp", {
                    object_type: object_type,
                    object_id: object_id,
                    editing: false,
                  });
                });
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="mr-3"
              >
                <path
                  d="M3.75 8L9 3M9 3L14.25 8M9 3V15"
                  stroke={contextData.dark_mode ? "white" : "black"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Move Up
            </button>

            <button
              title="Move Down"
              className="flex items-center px-4 py-3 text-lg bg-white hover:bg-slate-100 dark:bg-black dark:hover:bg-slate-800 border-none cursor-pointer w-full text-left text-black dark:text-white"
              onClick={(e) => {
                clickWrapper(e, () => {
                  dispatchCustomEvent("moveObjectDown", {
                    object_type: object_type,
                    object_id: object_id,
                    editing: false,
                  });
                });
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="mr-3"
              >
                <path
                  d="M3.75 10L9 15M9 15L14.25 10M9 15V3"
                  stroke={contextData.dark_mode ? "white" : "black"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Move Down
            </button>
          </React.Fragment>
        )}

        
        {showDuplicateButton && (
          <button
            title="Duplicate"
            className="flex items-center px-4 py-3 text-lg bg-white hover:bg-slate-100 dark:bg-black dark:hover:bg-slate-800 border-none cursor-pointer w-full text-left text-black dark:text-white"
            onClick={(e) => {
              clickWrapper(e, () => {
                dispatchCustomEvent("duplicateObject", {
                  object_type: object_type,
                  object_id: object_id,
                });
              });
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="mr-3"
            >
              <path
                d="M14.25 9.84375L14.25 4.49994C14.25 3.25729 13.2426 2.24992 12 2.24994L6.65625 2.25M9.75 15.75L4.6875 15.75C3.75552 15.75 3 14.9945 3 14.0625L3 6.75C3 5.81802 3.75552 5.0625 4.6875 5.0625L9.75 5.0625C10.682 5.0625 11.4375 5.81802 11.4375 6.75L11.4375 14.0625C11.4375 14.9945 10.682 15.75 9.75 15.75Z"
                stroke={contextData.dark_mode ? "white" : "black"}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            Duplicate
          </button>
        )}

        {showResetButton && (
          <button
            className="flex items-center px-4 py-3 text-lg bg-white hover:bg-slate-100 dark:bg-black dark:hover:bg-slate-800 border-none cursor-pointer w-full text-left text-black dark:text-white"
            onClick={(e) => {
              clickWrapper(e, () => {
                dispatchCustomEvent("resetStyles", {
                  object_type: popoverProps.object_type,
                  object_id: popoverProps.object_id,
                });
              });
            }}
            title="Reset To Theme Styles"
        >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              className="mr-3"
            >
              <path
                d="M4.87115 14.5C5.88914 17.6939 8.80463 20 12.2424 20C16.5268 20 20 16.4183 20 12C20 7.58172 16.5268 4 12.2424 4C9.37103 4 6.86399 5.60879 5.52267 8M7.87879 9H4V5"
                stroke={contextData.dark_mode ? `white` : "black"}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Reset Styles
          </button>
        )}

        {canSaveTemplate && (
          <button
            className="flex items-center px-4 py-3 text-lg bg-white hover:bg-slate-100 dark:bg-black dark:hover:bg-slate-800 border-none cursor-pointer w-full text-left text-black dark:text-white"
            onClick={(e) => {
              clickWrapper(e, () => {
                saveTemplate();
              });
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="mr-3"
            >
              <path
                d="M6.9375 19.875V14.8125C6.9375 14.1912 7.44118 13.6875 8.0625 13.6875H15.9375C16.5588 13.6875 17.0625 14.1912 17.0625 14.8125V20.4375M17.0625 4.125V6.375C17.0625 6.99632 16.5588 7.5 15.9375 7.5L8.0625 7.5C7.44118 7.5 6.9375 6.99632 6.9375 6.375L6.9375 3M20.4351 6.93513L17.0649 3.56487C16.7032 3.20319 16.2127 3 15.7012 3H4.92857C3.86344 3 3 3.86344 3 4.92857V19.0714C3 20.1366 3.86344 21 4.92857 21H19.0714C20.1366 21 21 20.1366 21 19.0714V8.29883C21 7.78734 20.7968 7.2968 20.4351 6.93513Z"
                stroke={contextData.dark_mode ? "white" : "black"}
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Save Template
          </button>
        )}

        {showRemoveButton && (
          <button
            title="Remove"
            className="flex items-center px-4 py-3 text-lg bg-white hover:bg-slate-100 dark:bg-black dark:hover:bg-slate-800 border-none cursor-pointer w-full text-left text-black dark:text-white"
            onClick={(e) => {
              clickWrapper(e, () => {
                dispatchCustomEvent("removeObject", {
                  object_type: object_type,
                  object_id: object_id,
                });
              });
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="mr-3"
            >
              <path
                d="M3 4.63235H15M6.75 2.25H11.25M7.5 12.5735V7.80882M10.5 12.5735V7.80882M11.625 15.75H6.375C5.54657 15.75 4.875 15.0389 4.875 14.1618L4.53255 5.45953C4.5148 5.00837 4.85544 4.63235 5.2819 4.63235H12.7181C13.1446 4.63235 13.4852 5.00837 13.4674 5.45953L13.125 14.1618C13.125 15.0389 12.4534 15.75 11.625 15.75Z"
                stroke={contextData.dark_mode ? "white" : "black"}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Remove
          </button>
        )}
      </div>
    </div>
  );
};

export default VariantBuilderCanvasSelectPopover;
